import { motion } from "framer-motion";
import { FC, ComponentType } from "react";
import { FaHome } from "react-icons/fa";
import { EASING } from "../../utils/easingUtils";

interface LocationDetailLoaderProps {
  icon?: ComponentType<{ className?: string }>; // Icon component prop
}

const DetailLoader: FC<LocationDetailLoaderProps> = ({ icon: Icon = FaHome }) => {
  // Animation variants for the icon and text container
  const iconVariants = {
    animate: {
      scale: [1, 1.2, 1], // Pulse effect
      rotate: [0, 5, -5, 0], // Subtle wiggle
      opacity: [0.7, 1, 0.7], // Fade in and out slightly
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: EASING.easeInOut,
      },
    },
  };

  // Animation for the text
  const textVariants = {
    animate: {
      opacity: [0.6, 1, 0.6], // Subtle fade effect
      scale: [1, 1.05, 1], // Slight scale for liveliness
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: EASING.easeInOut,
        delay: 0.2, // Slightly offset from icon animation
      },
    },
  };

  // Animation for surrounding circles (for a dynamic, orbiting effect)
  const circleVariants = {
    animate: {
      rotate: 360,
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: EASING.linear,
      },
    },
  };

  return (
    <div className="flex dark:bg-neutral-900">
      <motion.div
        className="relative flex items-center justify-center"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: EASING.easeOut }}
      >
        {/* Background orbiting circles for extra dynamism */}
        <motion.div
          className="absolute w-40 h-40 border-2 border-blue-200 rounded-full opacity-30"
          variants={circleVariants}
          animate="animate"
        />
        <motion.div
          className="absolute w-32 h-32 border-2 border-blue-300 rounded-full opacity-30"
          variants={circleVariants}
          animate="animate"
          style={{ animationDirection: "reverse" }}
        />
        {/* Icon and text container */}
        <motion.div variants={iconVariants} animate="animate" className="relative z-10 flex flex-col items-center">
          <Icon className="w-20 h-20 text-blue-500 drop-shadow-lg" />
          <motion.p
            variants={textVariants}
            animate="animate"
            className="mt-4 text-lg font-semibold text-blue-600 dark:text-blue-400"
          >
            Chargement en cours
          </motion.p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default DetailLoader;
